import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Users, Mail, Globe, Megaphone, Send, Plus, Trash2, 
  MessageCircle, Sparkles, Edit2, Check, Loader2, AlertCircle, Info 
} from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HELPERS = [
  {
    id: 'social',
    name: 'Social Media & Content',
    subtitle: 'Posts, reels scripts, captions, content ideas',
    icon: Megaphone,
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-700'
  },
  {
    id: 'sales',
    name: 'Sales & Email Outreach',
    subtitle: 'Cold emails, follow-ups, lead nurture scripts',
    icon: Mail,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700'
  },
  {
    id: 'website',
    name: 'Website & Landing Pages',
    subtitle: 'Homepage copy, landing sections, basic funnels',
    icon: Globe,
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700'
  },
  {
    id: 'networking',
    name: 'Presentations & Networking',
    subtitle: '30/45/60 second presentation scripts and networking pitches',
    icon: Users,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700'
  }
];

const DigiSquad = () => {
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  
  // State
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [activeHelper, setActiveHelper] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  const [formData, setFormData] = useState({
    ownerName: '',
    businessName: '',
    website: '',
    about: '',
    targetAudience: '',
    services: '',
    goals: '',
    brandTone: ''
  });

  // Social media state
  const [socialConnections, setSocialConnections] = useState({
    facebook_connected: false,
    instagram_connected: false,
    facebook_pages_count: 0,
    instagram_accounts_count: 0
  });
  const [showPostModal, setShowPostModal] = useState(false);
  const [postModalType, setPostModalType] = useState(null); // 'facebook' or 'instagram'
  const [postContent, setPostContent] = useState('');
  const [facebookPages, setFacebookPages] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    loadData();
    checkSocialConnections();
    
    // Check for OAuth callback results in URL
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    const message = params.get('message');
    
    if (connected === 'facebook') {
      toast.success('Facebook connected successfully!');
      checkSocialConnections(); // Refresh connection status
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (error) {
      if (error.includes('facebook')) {
        toast.error(
          `Facebook connection failed: ${message || 'Please check Facebook app configuration'}`,
          { duration: 5000 }
        );
      }
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      const [profileRes, conversationsRes] = await Promise.all([
        axios.get(`${API}/digi-squad/profile`, { headers: getAuthHeaders() }),
        axios.get(`${API}/digi-squad/conversations`, { headers: getAuthHeaders() })
      ]);
      
      setProfile(profileRes.data.profile);
      setConversations(conversationsRes.data.conversations || []);
      
      if (!profileRes.data.profile) {
        setShowProfileForm(true);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const checkSocialConnections = async () => {
    try {
      const response = await axios.get(`${API}/social-media/connection-status`, { 
        headers: getAuthHeaders() 
      });
      setSocialConnections(response.data);
    } catch (error) {
      console.error('Failed to check social connections:', error);
    }
  };

  const handleConnectFacebook = async () => {
    try {
      const response = await axios.get(`${API}/auth/facebook/login`);
      
      // Show a toast with instructions
      toast.info('Redirecting to Facebook... If you see an error, check the app configuration.', {
        duration: 3000
      });
      
      // Small delay to show the toast before redirect
      setTimeout(() => {
        window.location.href = response.data.auth_url;
      }, 500);
    } catch (error) {
      console.error('Facebook connection error:', error);
      toast.error(
        'Failed to initiate Facebook connection. Please contact the administrator to configure the Facebook App.',
        { duration: 5000 }
      );
    }
  };

  const handlePostToFacebook = async (content) => {
    try {
      // Load Facebook pages
      const response = await axios.get(`${API}/social-media/facebook-pages`, {
        headers: getAuthHeaders()
      });
      setFacebookPages(response.data.pages || []);
      setPostContent(content);
      setPostModalType('facebook');
      setShowPostModal(true);
    } catch (error) {
      toast.error('Failed to load Facebook pages');
    }
  };

  const handlePostToInstagram = async (content) => {
    try {
      // Load Instagram accounts
      const response = await axios.get(`${API}/social-media/instagram-accounts`, {
        headers: getAuthHeaders()
      });
      setInstagramAccounts(response.data.accounts || []);
      setPostContent(content);
      setPostModalType('instagram');
      setShowPostModal(true);
    } catch (error) {
      toast.error('Failed to load Instagram accounts');
    }
  };

  const confirmPost = async () => {
    if (postModalType === 'facebook' && !selectedPage) {
      toast.error('Please select a Facebook page');
      return;
    }
    if (postModalType === 'instagram' && (!selectedAccount || !imageUrl)) {
      toast.error('Please select an Instagram account and provide an image URL');
      return;
    }

    setPosting(true);
    try {
      if (postModalType === 'facebook') {
        await axios.post(
          `${API}/social-media/post-to-facebook`,
          {
            page_id: selectedPage,
            message: postContent,
            image_url: imageUrl || null
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Posted to Facebook successfully!');
      } else if (postModalType === 'instagram') {
        await axios.post(
          `${API}/social-media/post-to-instagram`,
          {
            instagram_account_id: selectedAccount,
            caption: postContent,
            image_url: imageUrl
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Posted to Instagram successfully!');
      }
      
      // Reset and close modal
      setShowPostModal(false);
      setSelectedPage('');
      setSelectedAccount('');
      setImageUrl('');
      setPostContent('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post to social media');
    } finally {
      setPosting(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/digi-squad/profile`,
        formData,
        { headers: getAuthHeaders() }
      );
      setProfile(response.data.profile);
      setShowProfileForm(false);
      toast.success('Business profile saved!');
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };

  const selectHelper = (helper) => {
    setActiveHelper(helper);
    setActiveConversation(null);
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await axios.get(
        `${API}/digi-squad/conversations/${conversationId}`,
        { headers: getAuthHeaders() }
      );
      setActiveConversation(response.data.conversation);
      
      // Find and set the helper
      const helper = HELPERS.find(h => h.id === response.data.conversation.helperId);
      setActiveHelper(helper);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !activeHelper) return;

    const userMessage = message;
    setMessage('');
    setSending(true);

    // Optimistically add user message to UI
    if (activeConversation) {
      const newMsg = {
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString()
      };
      setActiveConversation({
        ...activeConversation,
        messages: [...activeConversation.messages, newMsg]
      });
    }

    try {
      const response = await axios.post(
        `${API}/digi-squad/chat`,
        {
          helperId: activeHelper.id,
          conversationId: activeConversation?.id || null,
          message: userMessage
        },
        { headers: getAuthHeaders() }
      );

      console.log('Chat response:', response.data);

      if (!response.data.success) {
        throw new Error('API returned success: false');
      }

      // Update conversation with assistant response
      const updatedConversation = {
        id: response.data.conversationId,
        helperId: activeHelper.id,
        helperName: activeHelper.name,
        messages: [
          ...(activeConversation?.messages || []),
          {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
          },
          response.data.message
        ]
      };
      
      setActiveConversation(updatedConversation);
      
      // Reload conversations list
      loadData();
      
      toast.success('Response received!');
    } catch (error) {
      console.error('Chat error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;

    try {
      await axios.delete(
        `${API}/digi-squad/conversations/${conversationId}`,
        { headers: getAuthHeaders() }
      );
      
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
      }
      
      loadData();
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading Digi Squad...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                <Sparkles className="inline w-8 h-8 mr-2 text-purple-600" />
                Digi Squad
              </h1>
              <p className="text-slate-600">AI Employee Team for Your Business</p>
            </div>
            {profile && (
              <Button
                onClick={() => setShowProfileForm(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                Edit Business Profile
              </Button>
            )}
          </div>
        </div>

        {/* Social Media Connections */}
        {profile && (
          <Card className="mb-6 p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Megaphone className="w-5 h-5 text-blue-600" />
              Social Media Connections
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleConnectFacebook}
                variant={socialConnections.facebook_connected ? "outline" : "default"}
                className={`flex items-center gap-2 ${
                  socialConnections.facebook_connected 
                    ? 'bg-green-50 border-green-500 text-green-700 hover:bg-green-100' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {socialConnections.facebook_connected ? (
                  <>
                    <Check className="w-4 h-4" />
                    Facebook Connected ({socialConnections.facebook_pages_count} pages)
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Connect Facebook
                  </>
                )}
              </Button>

              <Button
                onClick={() => toast.info('Instagram connection coming soon!')}
                variant={socialConnections.instagram_connected ? "outline" : "default"}
                className={`flex items-center gap-2 ${
                  socialConnections.instagram_connected 
                    ? 'bg-green-50 border-green-500 text-green-700 hover:bg-green-100' 
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                }`}
              >
                {socialConnections.instagram_connected ? (
                  <>
                    <Check className="w-4 h-4" />
                    Instagram Connected ({socialConnections.instagram_accounts_count} accounts)
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Connect Instagram
                  </>
                )}
              </Button>

              <div className="ml-auto flex items-center gap-2 text-sm text-slate-600">
                <span>Post AI-generated content directly to your social media</span>
              </div>
            </div>
          </Card>
        )}

        {/* Business Profile Form Modal */}
        {showProfileForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">Business Profile</h2>
                <p className="text-sm text-slate-600 mb-6">
                  Your AI employees use this information to create personalized content, emails, and presentations.
                </p>

                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Your Name *</Label>
                      <Input
                        value={formData.ownerName}
                        onChange={(e) => setFormData({...formData, ownerName: e.target.value})}
                        placeholder="e.g., Akhil Gutta"
                        required
                      />
                    </div>
                    <div>
                      <Label>Business Name *</Label>
                      <Input
                        value={formData.businessName}
                        onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                        placeholder="e.g., Portrait Chocolate"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Website</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  <div>
                    <Label>About Your Business *</Label>
                    <Textarea
                      value={formData.about}
                      onChange={(e) => setFormData({...formData, about: e.target.value})}
                      placeholder="What do you do? What makes you unique?"
                      rows={3}
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Target Audience</Label>
                      <Textarea
                        value={formData.targetAudience}
                        onChange={(e) => setFormData({...formData, targetAudience: e.target.value})}
                        placeholder="Who do you serve?"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Main Services/Offers</Label>
                      <Textarea
                        value={formData.services}
                        onChange={(e) => setFormData({...formData, services: e.target.value})}
                        placeholder="Your key products/services"
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Goals with AI Employees</Label>
                      <Textarea
                        value={formData.goals}
                        onChange={(e) => setFormData({...formData, goals: e.target.value})}
                        placeholder="e.g., get more leads, save time on content"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Brand Tone</Label>
                      <Input
                        value={formData.brandTone}
                        onChange={(e) => setFormData({...formData, brandTone: e.target.value})}
                        placeholder="e.g., fun & playful, luxury, professional"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                      <Check className="w-4 h-4 mr-2" />
                      Save Profile
                    </Button>
                    {profile && (
                      <Button type="button" variant="outline" onClick={() => setShowProfileForm(false)}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            </Card>
          </div>
        )}

        {/* Main Content */}
        {!showProfileForm && (
          <div className="grid md:grid-cols-12 gap-6">
            {/* Left: AI Helpers */}
            <div className="md:col-span-4 space-y-4">
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Your AI Employees</h3>
                <div className="space-y-2">
                  {HELPERS.map((helper) => {
                    const Icon = helper.icon;
                    return (
                      <button
                        key={helper.id}
                        onClick={() => selectHelper(helper)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          activeHelper?.id === helper.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${helper.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm">{helper.name}</h4>
                            <p className="text-xs text-slate-600 mt-1">{helper.subtitle}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Card>

              {/* Conversation History */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Recent Conversations</h3>
                {conversations.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No conversations yet</p>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => loadConversation(conv.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          activeConversation?.id === conv.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-500">{conv.helperName}</p>
                            <p className="text-sm font-medium truncate">{conv.title}</p>
                          </div>
                          <button
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="ml-2 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* Right: Chat Interface */}
            <div className="md:col-span-8">
              <Card className="h-[calc(100vh-250px)] flex flex-col">
                {/* Chat Header */}
                <div className="p-4 border-b">
                  {activeHelper ? (
                    <div className="flex items-center gap-3">
                      {React.createElement(activeHelper.icon, { className: `w-6 h-6 text-${activeHelper.textColor}` })}
                      <div>
                        <h3 className="font-semibold">{activeHelper.name}</h3>
                        <p className="text-xs text-slate-500">{activeHelper.subtitle}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500">Select an AI employee to start chatting</p>
                  )}
                </div>

                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {!activeHelper ? (
                    <div className="text-center py-12">
                      <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Select an AI employee from the left to start</p>
                    </div>
                  ) : !activeConversation ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Start a new conversation with {activeHelper.name}</p>
                      <p className="text-xs text-slate-400 mt-2">Ask for help with content, emails, scripts, and more!</p>
                    </div>
                  ) : (
                    <>
                      {activeConversation.messages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] ${msg.role === 'user' ? '' : 'space-y-2'}`}>
                            <div
                              className={`rounded-2xl px-4 py-3 ${
                                msg.role === 'user'
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-slate-100 text-slate-900'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            
                            {/* Social Media Posting Buttons for AI responses */}
                            {msg.role === 'assistant' && (
                              <div className="flex gap-2 pl-2 flex-wrap">
                                {/* WhatsApp Share - Always visible */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const text = encodeURIComponent(msg.content);
                                    window.open(`https://wa.me/?text=${text}`, '_blank');
                                  }}
                                  className="text-xs bg-green-50 hover:bg-green-100 border-green-300 text-green-700"
                                >
                                  💬 Share on WhatsApp
                                </Button>
                                
                                {/* Copy to Clipboard */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(msg.content);
                                    toast.success('Copied to clipboard!');
                                  }}
                                  className="text-xs bg-slate-50 hover:bg-slate-100 border-slate-300 text-slate-700"
                                >
                                  📋 Copy
                                </Button>
                                
                                {/* Facebook & Instagram if connected */}
                                {socialConnections.facebook_connected && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handlePostToFacebook(msg.content)}
                                    className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-700"
                                  >
                                    📘 Post to Facebook
                                  </Button>
                                )}
                                {socialConnections.instagram_connected && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handlePostToInstagram(msg.content)}
                                    className="text-xs bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border-purple-300 text-purple-700"
                                  >
                                    📷 Post to Instagram
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </>
                  )}
                </div>

                {/* Chat Input */}
                <form onSubmit={sendMessage} className="p-4 border-t">
                  <div className="flex gap-2">
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={activeHelper ? `Ask ${activeHelper.name} for help...` : 'Select a helper first'}
                      className="flex-1 resize-none"
                      rows={2}
                      disabled={!activeHelper || sending}
                    />
                    <Button
                      type="submit"
                      disabled={!activeHelper || !message.trim() || sending}
                      className="bg-purple-600 hover:bg-purple-700 self-end"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          </div>
        )}

        {/* Post to Social Media Modal */}
        {showPostModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">
                  {postModalType === 'facebook' ? '📘 Post to Facebook' : '📷 Post to Instagram'}
                </h2>

                <div className="space-y-4">
                  {/* Select Page/Account */}
                  {postModalType === 'facebook' ? (
                    <div>
                      <Label>Select Facebook Page *</Label>
                      <select
                        value={selectedPage}
                        onChange={(e) => setSelectedPage(e.target.value)}
                        className="w-full mt-1 p-2 border rounded-md"
                        required
                      >
                        <option value="">-- Select a page --</option>
                        {facebookPages.map((page) => (
                          <option key={page.page_id} value={page.page_id}>
                            {page.page_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <Label>Select Instagram Account *</Label>
                      <select
                        value={selectedAccount}
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        className="w-full mt-1 p-2 border rounded-md"
                        required
                      >
                        <option value="">-- Select an account --</option>
                        {instagramAccounts.map((account) => (
                          <option key={account.instagram_account_id} value={account.instagram_account_id}>
                            @{account.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Post Content */}
                  <div>
                    <Label>Post Content</Label>
                    <Textarea
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      rows={6}
                      className="mt-1"
                      placeholder="Your post content..."
                    />
                  </div>

                  {/* Image URL */}
                  <div>
                    <Label>
                      Image URL {postModalType === 'instagram' && '*'}
                      {postModalType === 'facebook' && ' (Optional)'}
                    </Label>
                    <Input
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="mt-1"
                      required={postModalType === 'instagram'}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {postModalType === 'instagram' 
                        ? 'Instagram requires an image. Use a public URL.'
                        : 'Optional: Add an image to your Facebook post'}
                    </p>
                  </div>

                  {/* Preview */}
                  {imageUrl && (
                    <div>
                      <Label>Image Preview</Label>
                      <div className="mt-2 border rounded-lg overflow-hidden">
                        <img 
                          src={imageUrl} 
                          alt="Preview" 
                          className="w-full max-h-64 object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            toast.error('Invalid image URL');
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={confirmPost}
                      disabled={posting}
                      className={`flex-1 ${
                        postModalType === 'facebook'
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                      }`}
                    >
                      {posting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Posting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Post Now
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPostModal(false);
                        setSelectedPage('');
                        setSelectedAccount('');
                        setImageUrl('');
                      }}
                      disabled={posting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigiSquad;
